import { Box, List, Text } from '@dixa/lib-shared-habla';
import { Message } from 'lib-shared-legacy-types';
import { memo, useEffect, useState } from 'react';

import { customFormsApi } from 'apis/customForms';
import { FormSubmissionData } from 'app/views/layout/views/settings/views/customForms';
import { formSubmissionConverter } from 'app/views/layout/views/settings/views/customForms/hooks/useCustomForms/formSubmissionConverter';

import { Message as MessageComponent } from '../message/message';

import TextMessage, { TextMessageProps } from './TextMessage';
import { createAttachedFiles } from './utils/AttachedFile';
import { useTranslatePlainTextConversationMessage } from './utils/useTranslatePlainTextConversationMessage';

export type MessageCustomFormProps = TextMessageProps & {
  message: Message & { form_id: string; form_version: string };
};

export const MessageCustomForm = memo(function MessageWidgetChatForm(
  props: MessageCustomFormProps,
) {
  const { text, form_id, form_version, form_submission_id } = props.message;

  const [formSubmissionData, setFormSubmissionData] =
    useState<FormSubmissionData | null>(null);

  const { button, translation } = useTranslatePlainTextConversationMessage({
    text: text ?? undefined,
    originalLanguageCode: props.message.language ?? null,
  });

  const { isError, isLoading, data } = customFormsApi.useReadFormQuery({
    form_id,
    version: form_version,
  });

  const [readFormSubmission] =
    customFormsApi.useLazyReadFormSubmissionDataQuery();

  useEffect(() => {
    async function fetchFormSubmissionData() {
      if (!form_submission_id) {
        return;
      }

      try {
        const response = await readFormSubmission({
          form_id,
          form_version,
          submission_id: form_submission_id,
        }).unwrap();

        setFormSubmissionData(
          formSubmissionConverter.toInternal(response.data),
        );
      } catch (error) {
        setFormSubmissionData(null);
      }
    }

    fetchFormSubmissionData();
  }, [form_submission_id, readFormSubmission, form_id, form_version]);

  if (isLoading) {
    return (
      <MessageComponent
        author={props.message.author}
        content={<div>Loading the form...</div>}
      />
    );
  }

  if (isError) {
    return <TextMessage author={props.author} message={props.message} />;
  }

  const form = data?.data;

  if (!form) {
    // Fall back to the text-based representation of the form
    return (
      <TextMessage
        {...props}
        attachments={createAttachedFiles(props.message.attached_files)}
        gutterButtons={button}
        translation={translation}
      />
    );
  }

  return (
    <MessageComponent
      author={props.message.author}
      content={
        <Box padding="100 0">
          <Text.Paragraph variant="2">Form: {form.label}</Text.Paragraph>
          <List>
            {form.attributes?.map(({ id, label }) => (
              <List.KeyValue
                key={id}
                pair={{
                  key: label,
                  value: formSubmissionData?.submissionData?.[id] ?? '',
                }}
              />
            ))}
          </List>
        </Box>
      }
    />
  );
});

export function isMessengerCustomFormMessage(
  message: Message,
): message is MessageCustomFormProps['message'] {
  return Boolean(message.form_id) && Boolean(message.form_version);
}
